// Browser SIP/WebRTC audio calling over a WebSocket transport (e.g. Asterisk/PJSIP with
// a WSS transport enabled). Ported from the chatbot-interface project's sipAudio.ts so the
// landing page demo call can talk to the same kind of backend.
//
// Requires the `sip.js` package: npm install sip.js

import {useEffect, useMemo, useRef, useState} from "react";
import {UserAgent, Web} from "sip.js";

const DEFAULT_REGISTER_EXPIRES = "300";

function trimTrailingSlash(value) {
  return value.replace(/\/+$/, "");
}

function trimLeadingSlash(value) {
  return value.replace(/^\/+/, "");
}

export function getDefaultSipAudioConfig() {
  return {
    enabled: process.env.REACT_APP_SIP_ENABLED === "true",
    serverHost: process.env.REACT_APP_SIP_SERVER_HOST || "",
    serverPort: process.env.REACT_APP_SIP_SERVER_PORT || "8089",
    serverPath: process.env.REACT_APP_SIP_SERVER_PATH || "/ws",
    secure: process.env.REACT_APP_SIP_SERVER_SECURE !== "false",
    domain: process.env.REACT_APP_SIP_DOMAIN || "",
    username: process.env.REACT_APP_SIP_USERNAME || "",
    password: process.env.REACT_APP_SIP_PASSWORD || "",
    displayName: process.env.REACT_APP_SIP_DISPLAY_NAME || "",
    destination: process.env.REACT_APP_SIP_DESTINATION || "",
    // registering needs the password; without one the demo only places calls (Asterisk must accept them unauthenticated)
    autoRegister: process.env.REACT_APP_SIP_AUTO_REGISTER !== "false" && Boolean(process.env.REACT_APP_SIP_PASSWORD),
    registerExpires: process.env.REACT_APP_SIP_REGISTER_EXPIRES || DEFAULT_REGISTER_EXPIRES,
    // Calls through the chatbot backend's SIP proxy: the backend holds the PBX login, the browser only gets a
    // single-use token per call. The server, domain, username and password above are then not used.
    proxy: process.env.REACT_APP_SIP_PROXY_ENABLED === "true",
    proxyBaseUrl: process.env.REACT_APP_CHATBOT_BACKEND || "",
  };
}

// Proxy mode needs only REACT_APP_SIP_PROXY_ENABLED and the chatbot backend; REACT_APP_SIP_ENABLED turns on the
// direct connection. The password is optional: an outgoing-only demo calls without registering.
export function isSipConfigured(config) {
  if (config.proxy) return Boolean(config.proxyBaseUrl.trim());
  return Boolean(
    config.enabled &&
      config.serverHost.trim() &&
      config.serverPort.trim() &&
      config.domain.trim() &&
      config.username.trim()
  );
}

export function buildSipServerUrl(config) {
  const protocol = config.secure ? "wss" : "ws";
  const host = trimTrailingSlash(config.serverHost.trim());
  const normalizedPath = trimLeadingSlash(config.serverPath.trim() || "ws");
  return `${protocol}://${host}:${config.serverPort.trim()}/${normalizedPath}`;
}

export function buildSipAddress(username, domain) {
  return `sip:${username.trim()}@${domain.trim()}`;
}

export function normalizeSipTarget(target, domain) {
  const trimmedTarget = target.trim();
  if (!trimmedTarget) return "";
  if (trimmedTarget.startsWith("sip:")) return trimmedTarget;
  if (trimmedTarget.includes("@")) return `sip:${trimmedTarget}`;
  return `sip:${trimmedTarget}@${domain.trim()}`;
}

const minutesLabel = (seconds) => {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return minutes === 1 ? "1 minute" : `${minutes} minutes`;
};

/**
 * Asks the chatbot backend's SIP proxy for a single-use call token (valid 60s, bound to this IP).
 * Resolves to {token, expiresAt, wsUrl, sipUri, displayName, extensions}.
 * Throws an Error with a message for the user: the call limit per hour (429) or the proxy turned off (404).
 */
export async function fetchSipProxyToken(config) {
  const url = `${trimTrailingSlash(config.proxyBaseUrl.trim())}/api/sip-proxy/token`;
  let response;
  try {
    response = await fetch(url, {method: "POST"});
  } catch {
    throw new Error("Could not reach the call service. Please try again.");
  }
  const body = await response.json().catch(() => ({}));
  if (response.status === 429) {
    const seconds = Number(body.retryAfterSeconds ?? response.headers.get("Retry-After"));
    throw new Error(Number.isFinite(seconds) && seconds > 0
      ? `Too many calls. Please try again in ${minutesLabel(seconds)}.`
      : body.message || "Too many calls. Please try again later.");
  }
  if (response.status === 404) throw new Error(body.message || "The live call demo is turned off.");
  if (!response.ok || !body.token || !body.wsUrl || !body.sipUri) {
    throw new Error(body.message || "Could not start the call. Please try again.");
  }
  return body;
}

// sip:user@host -> host
const domainOf = (sipUri) => sipUri.replace(/^sips?:/, "").split("@")[1]?.split(/[;:>]/)[0] || "";

function registerExpiry(config) {
  const parsed = Number(config.registerExpires);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : Number(DEFAULT_REGISTER_EXPIRES);
}

/**
 * React hook wrapping sip.js's SimpleUser for a single audio-only call.
 * Mirrors the shape used by chatbot-interface's useSipAudioClient.
 */
export function useSipAudioClient(config) {
  const remoteAudioRef = useRef(null);
  const simpleUserRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isHeld, setIsHeld] = useState(false);
  const [hasIncomingCall, setHasIncomingCall] = useState(false);
  // Bumped whenever the underlying local/remote MediaStreams might have changed, so consumers
  // that read them via getStreams() (e.g. to feed a live audio visualizer) know to re-check.
  const [mediaTick, setMediaTick] = useState(0);

  const configSignature = useMemo(() => JSON.stringify(config), [config]);

  useEffect(() => {
    return () => {
      void simpleUserRef.current?.disconnect();
      simpleUserRef.current = null;
    };
  }, []);

  const configureRemoteAudioElement = () => {
    const element = remoteAudioRef.current;
    if (!element) return;
    element.autoplay = true;
    element.playsInline = true;
    element.muted = false;
    element.volume = 1;
  };

  const syncAndPlayRemoteAudio = async () => {
    const simpleUser = simpleUserRef.current;
    const element = remoteAudioRef.current;
    if (!simpleUser || !element) return;

    configureRemoteAudioElement();

    const remoteStream = simpleUser.remoteMediaStream;
    if (remoteStream && element.srcObject !== remoteStream) {
      element.srcObject = remoteStream;
    }

    try {
      await element.play();
      setError(null);
    } catch (playError) {
      setError(playError instanceof Error ? `Remote audio playback failed: ${playError.message}` : "Remote audio playback failed.");
    }
  };

  useEffect(() => {
    if (!simpleUserRef.current) return;
    void simpleUserRef.current.disconnect();
    simpleUserRef.current = null;
    setStatus("idle");
    setError(null);
    setIsConnected(false);
    setIsRegistered(false);
    setIsMuted(false);
    setIsHeld(false);
    setHasIncomingCall(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configSignature]);

  useEffect(() => {
    configureRemoteAudioElement();
  }, []);

  useEffect(() => {
    if (status !== "in-call") return;
    void syncAndPlayRemoteAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // proxySession: {serverUrl, aor, displayName} of a proxy call token; without it the PBX is dialled directly
  const ensureSimpleUser = (proxySession) => {
    if (simpleUserRef.current) return simpleUserRef.current;

    if (!isSipConfigured(config)) {
      throw new Error("SIP audio settings are incomplete.");
    }

    const aor = proxySession ? proxySession.aor : buildSipAddress(config.username, config.domain);
    const uri = UserAgent.makeURI(aor);
    if (!uri) {
      throw new Error("Invalid SIP address configuration.");
    }

    const simpleUser = new Web.SimpleUser(proxySession ? proxySession.serverUrl : buildSipServerUrl(config), {
      aor,
      media: {
        constraints: {audio: true, video: false},
        remote: {audio: remoteAudioRef.current ?? undefined},
      },
      registererOptions: {expires: registerExpiry(config)},
      // a proxy token is single use, so a dropped connection can't be reopened with it
      reconnectionAttempts: proxySession ? 0 : undefined,
      userAgentOptions: proxySession ? {
        // the proxy logs in to the PBX itself: no credentials in the browser
        uri,
        displayName: proxySession.displayName,
        logBuiltinEnabled: false,
        logConfiguration: false,
      } : {
        uri,
        authorizationUsername: config.username.trim(),
        authorizationPassword: config.password || undefined,
        displayName: config.displayName.trim() || config.username.trim(),
        logBuiltinEnabled: false,
        logConfiguration: false,
      },
      delegate: {
        onServerConnect: () => {
          setIsConnected(true);
          setStatus((s) => (s === "registering" ? s : "connected"));
          setError(null);
        },
        onServerDisconnect: (disconnectError) => {
          // a proxy connection already replaced or closed after its call
          if (proxySession && simpleUserRef.current !== simpleUser) return;
          setIsConnected(false);
          setIsRegistered(false);
          setHasIncomingCall(false);
          setStatus(disconnectError ? "error" : "idle");
          setError(disconnectError?.message || null);
        },
        onRegistered: () => {
          setIsRegistered(true);
          setStatus("registered");
        },
        onUnregistered: () => {
          setIsRegistered(false);
          setStatus((s) => (s === "in-call" ? s : "connected"));
        },
        onCallCreated: () => {
          setHasIncomingCall(false);
          setStatus("calling");
          setMediaTick((t) => t + 1);
        },
        onCallReceived: () => {
          setHasIncomingCall(true);
          setStatus("incoming");
        },
        onCallAnswered: () => {
          setHasIncomingCall(false);
          setStatus("in-call");
          setError(null);
          void syncAndPlayRemoteAudio();
          setMediaTick((t) => t + 1);
        },
        onCallHangup: () => {
          setHasIncomingCall(false);
          setIsMuted(false);
          setIsHeld(false);
          // the proxy connection is for this one call: close it ourselves, so its closing isn't reported as an error
          if (proxySession && simpleUserRef.current === simpleUser) {
            simpleUserRef.current = null;
            void simpleUser.disconnect().catch(() => undefined);
            setIsConnected(false);
            setStatus("idle");
            setMediaTick((t) => t + 1);
            return;
          }
          setStatus((s) => {
            void s;
            return isRegistered ? "registered" : isConnected ? "connected" : "idle";
          });
          setMediaTick((t) => t + 1);
        },
        onCallHold: (held) => setIsHeld(held),
      },
    });

    simpleUserRef.current = simpleUser;
    return simpleUser;
  };

  const connect = async () => {
    try {
      setError(null);
      setStatus("connecting");
      const simpleUser = ensureSimpleUser();
      if (!simpleUser.isConnected()) {
        await simpleUser.connect();
      }
      if (config.autoRegister && !isRegistered) {
        setStatus("registering");
        await simpleUser.register();
      } else {
        setStatus(simpleUser.isConnected() ? "connected" : "idle");
      }
    } catch (connectError) {
      setError(connectError instanceof Error ? connectError.message : "Failed to connect.");
      setStatus("error");
      throw connectError;
    }
  };

  const disconnect = async () => {
    const simpleUser = simpleUserRef.current;
    if (!simpleUser) return;
    await simpleUser.disconnect();
    simpleUserRef.current = null;
    setStatus("idle");
    setError(null);
    setIsConnected(false);
    setIsRegistered(false);
    setIsMuted(false);
    setIsHeld(false);
    setHasIncomingCall(false);
  };

  const register = async () => {
    try {
      const simpleUser = ensureSimpleUser();
      if (!simpleUser.isConnected()) await connect();
      setStatus("registering");
      await simpleUser.register();
    } catch (registerError) {
      setError(registerError instanceof Error ? registerError.message : "Failed to register.");
      setStatus("error");
      throw registerError;
    }
  };

  // Proxy call: a fresh token and a new connection every time (the proxy allows one call per connection)
  const callThroughProxy = async (destination) => {
    if (simpleUserRef.current) {
      await simpleUserRef.current.disconnect().catch(() => undefined);
      simpleUserRef.current = null;
    }
    setError(null);
    setStatus("connecting");
    const session = await fetchSipProxyToken(config);
    const target = normalizeSipTarget(destination, domainOf(session.sipUri));
    if (!target) throw new Error("SIP destination is required.");

    const simpleUser = ensureSimpleUser({
      serverUrl: `${session.wsUrl}${session.wsUrl.includes("?") ? "&" : "?"}token=${encodeURIComponent(session.token)}`,
      aor: session.sipUri,
      displayName: session.displayName || "",
    });
    await simpleUser.connect();
    setStatus("calling");
    await simpleUser.call(target);
  };

  const call = async (destinationOverride) => {
    try {
      if (config.proxy) {
        await callThroughProxy(destinationOverride || config.destination);
        return;
      }
      const target = normalizeSipTarget(destinationOverride || config.destination, config.domain);
      if (!target) throw new Error("SIP destination is required.");

      const simpleUser = ensureSimpleUser();
      if (!simpleUser.isConnected()) await connect();
      if (config.autoRegister && !isRegistered) await register();

      setStatus("calling");
      await simpleUser.call(target);
    } catch (callError) {
      setError(callError instanceof Error ? callError.message : "Failed to start call.");
      setStatus("error");
      throw callError;
    }
  };

  const hangup = async () => {
    const simpleUser = simpleUserRef.current;
    if (!simpleUser) return;
    await simpleUser.hangup();
    setHasIncomingCall(false);
    setIsMuted(false);
    setIsHeld(false);
    setStatus(isRegistered ? "registered" : isConnected ? "connected" : "idle");
  };

  const answer = async () => {
    const simpleUser = simpleUserRef.current;
    if (!simpleUser) return;
    await simpleUser.answer();
    setHasIncomingCall(false);
  };

  const decline = async () => {
    const simpleUser = simpleUserRef.current;
    if (!simpleUser) return;
    await simpleUser.decline();
    setHasIncomingCall(false);
    setStatus(isRegistered ? "registered" : isConnected ? "connected" : "idle");
  };

  const toggleMute = () => {
    const simpleUser = simpleUserRef.current;
    if (!simpleUser) return;
    if (simpleUser.isMuted()) {
      simpleUser.unmute();
      setIsMuted(false);
      return;
    }
    simpleUser.mute();
    setIsMuted(true);
  };

  const toggleHold = async () => {
    const simpleUser = simpleUserRef.current;
    if (!simpleUser) return;
    if (simpleUser.isHeld()) {
      await simpleUser.unhold();
      setIsHeld(false);
      return;
    }
    await simpleUser.hold();
    setIsHeld(true);
  };

  /**
   * Returns the raw local (mic) and remote (far end) MediaStreams for the current call, if
   * any — e.g. to feed a live audio visualizer. Re-run this after `mediaTick` changes rather
   * than caching the result, since sip.js may replace these streams over a call's lifetime.
   */
  const getStreams = () => ({
    local: simpleUserRef.current?.localMediaStream,
    remote: simpleUserRef.current?.remoteMediaStream,
  });

  /** Fully tears the call/connection down; safe to call even if nothing was started. */
  const endEverything = async () => {
    const simpleUser = simpleUserRef.current;
    if (!simpleUser) return;
    try {
      if (["calling", "incoming", "in-call"].includes(status)) {
        await simpleUser.hangup().catch(() => undefined);
      }
      await simpleUser.disconnect().catch(() => undefined);
    } finally {
      simpleUserRef.current = null;
      setStatus("idle");
      setIsConnected(false);
      setIsRegistered(false);
      setIsMuted(false);
      setIsHeld(false);
      setHasIncomingCall(false);
      setMediaTick((t) => t + 1);
    }
  };

  return {
    remoteAudioRef,
    status,
    error,
    isConnected,
    isRegistered,
    isMuted,
    isHeld,
    hasIncomingCall,
    mediaTick,
    getStreams,
    connect,
    disconnect,
    register,
    call,
    hangup,
    answer,
    decline,
    toggleMute,
    toggleHold,
    endEverything,
  };
}
